// src/pages/Dashboard.tsx

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../api/axiosInstance';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useAppTheme } from '../themes/ThemeContext';
import {
    Container, Typography, Box, Grid, Card, CardHeader, CardContent,
    List, ListItem, ListItemText, ListItemSecondaryAction,
    IconButton, Popover, MenuItem, Table, TableBody, TableCell, TableRow,
    TableHead, LinearProgress, Divider, Tooltip,
    ListItemButton
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

interface Widget {
    widgetId: string;
    columnNumber: number;
    widgetType: string;
}

interface Row {
    rowId: string;
    rowNumber: number;
    columnCount: number;
    layoutStyle: string;
    widgets: Widget[];
}

interface DashboardData {
    dashboardId: string;
    dashboardName: string;
    rows: Row[];
}

interface TableData {
    tableID: string;
    tableName: string;
    deleted?: boolean;
    active?: boolean;
}

interface SavedFilter {
    savedFilterID: string;
    tableID: string;
    name: string;
    deleted: boolean;
}

const Dashboard: React.FC = () => {
    const [dashboard, setDashboard] = useState<DashboardData | null>(null);
    const [tables, setTables] = useState<TableData[]>([]);
    const [savedFilters, setSavedFilters] = useState<SavedFilter[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [selectedItem, setSelectedItem] = useState<{ item: any; type: 'table' | 'filter' | 'form' | 'report' } | null>(null);


    const navigate = useNavigate();
    const { mode } = useAppTheme();

    useEffect(() => {
        const user = localStorage.getItem('user');
        if (!user) {
            navigate('/login');
            return;
        }
        loadDashboard();
    }, []);

    const loadDashboard = async () => {
        setLoading(true);
        try {
          const user = JSON.parse(localStorage.getItem('user') || '{}');
          const headers = { headers: { authToken: user.user?.authtoken, sessionId: user.user?.sessionId } };
      
          // Step 1: Always load the dashboard itself
          const dashboardRes = await API.get('/dashboard/default', headers);
          const dashboardData = dashboardRes.data;
          setDashboard(dashboardData);
      
          if (!dashboardData || !Array.isArray(dashboardData.rows)) {
            console.warn('Empty or invalid dashboard data.');
            return;
          }
      
          // Step 2: Find all widget types needed
          const widgetTypes = dashboardData.rows.flatMap((row: any) =>
            (row.widgets || []).map((widget: any) => widget.widgetType?.toUpperCase())
          );
      
          // Step 3: Conditionally load Tables if needed
          if (widgetTypes.includes('TABLES')) {
            try {
              const tablesRes = await API.get('/tables', headers);
              const activeTables = (tablesRes.data || []).filter((t: any) => !t.deleted && t.active !== false);
              setTables(activeTables);
            } catch (tablesError) {
              console.error('Failed to load tables.', tablesError);
            }
          }
      
          // Step 4: Conditionally load My Filters if needed
          if (widgetTypes.includes('MYFILTERS')) {
            try {
              const filtersRes = await API.get('/tables/my-saved-filters', headers);
              const activeFilters = (filtersRes.data || []).filter((f: any) => !f.deleted);
              setSavedFilters(activeFilters);
            } catch (filtersError) {
              console.error('Failed to load saved filters.', filtersError);
            }
          }
      
        } catch (error) {
          console.warn('Dashboard failed to load.', error);
        } finally {
          setLoading(false);
        }
      };
      

    const handleOpen = (item: any, type: 'table' | 'filter' | 'form' | 'report') => {
        if (type === 'table') {
            navigate(`/table/${item.tableID}/view`);
        } else if (type === 'filter') {
            if (item) {
                navigate(`/table/${item.tableID}/view`, { state: { savedFilter: item } });
              }
              handleClosePopover();
        }
        handleClosePopover();
    };

    const handleListClick = (event: React.MouseEvent<HTMLElement>, item: any, type: 'table' | 'filter' | 'form' | 'report') => {
        setAnchorEl(event.currentTarget);
        setSelectedItem({ item, type });
    };

    const handleClosePopover = () => {
        setAnchorEl(null);
        setSelectedItem(null);
    };

    const renderListWidget = (items: any[], itemType: 'table' | 'filter' | 'form' | 'report') => {
        const maxItems = 5;
        const displayed = items.slice(0, maxItems);

        return (
            <List dense disablePadding sx={{ maxHeight: 300, overflowY: 'auto' }}>
                {displayed.map((item) => (
                    <ListItemButton
                    key={item.tableID || item.savedFilterID}
                    onClick={(e) => handleListClick(e, item, itemType)} 
                    onDoubleClick={() => handleOpen(item, itemType)}
                    sx={{ py: 0.5 }}
                  >
                    <ListItemText
                      primary={item.tableName || item.name}
                      primaryTypographyProps={{ fontSize: "0.9rem" }}
                    />
                    <ListItemSecondaryAction>
                      <Tooltip title="Open">
                        <IconButton edge="end" size="small" onClick={() => handleOpen(item, itemType)}>
                          <OpenInNewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </ListItemSecondaryAction>
                  </ListItemButton>
                  
                ))}
                {items.length > maxItems && (
                    <Typography variant="caption" align="center" display="block" mt={1}>
                        + {items.length - maxItems} more...
                    </Typography>
                )}
            </List>
        );
    };

    const renderWidget = (widget: Widget) => {
        switch (widget.widgetType.toUpperCase()) {
            case 'TASKS':
                return (
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Task</TableCell>
                                <TableCell>Due</TableCell>
                                <TableCell>Status</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            <TableRow>
                                <TableCell colSpan={3} align="center">
                                    No current tasks
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                );
            case 'TABLES':
                return renderListWidget(tables, 'table');
            case 'MYFILTERS':
                return renderListWidget(savedFilters, 'filter');
            case 'FORMS':
                return (
                    <Box textAlign="center" py={2}>
                        <Typography variant="body2" color="text.secondary">
                            No forms found. When created, your forms will appear here.
                        </Typography>
                    </Box>
                );
            case 'JOBS':
            case 'SYSTEM STATUS':
                return (
                    <Box>
                        <Typography variant="body2">Active System Tasks: <strong>0</strong></Typography>
                        <Typography variant="body2">Pending System Tasks: <strong>0</strong></Typography>
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="body2" gutterBottom>Data Usage</Typography>
                        <Box display="flex" alignItems="center" gap={1}>
                            <LinearProgress variant="determinate" value={10} sx={{ flexGrow: 1 }} />
                            <Typography variant="caption">10%</Typography>
                        </Box>
                    </Box>
                );
            default:
                return <Typography variant="body2" color="text.secondary">Unknown widget type</Typography>;
        }
    };

    return (
        <Box
            display="flex"
            flexDirection="column"
            minHeight="100vh"
            bgcolor={mode === 'dark' ? '#1e1e1e' : '#f5f5f5'}
            color={mode === 'dark' ? '#e0e0e0' : '#333'}
        >
            <Header />

            <Container maxWidth="lg" sx={{ mt: 10, mb: 4, flex: 1 }}>
                <Typography variant="h4" align="center" gutterBottom>
                    {dashboard?.dashboardName || 'My Dashboard'}
                </Typography>

                {loading ? (
                    <Typography align="center">Loading your dashboard...</Typography>
                ) : dashboard?.rows?.length ? (
                    dashboard.rows
                        .sort((a, b) => a.rowNumber - b.rowNumber)
                        .map((row) => (
                            <Grid container spacing={3} key={row.rowId} sx={{ mb: 2 }}>
                                {row.widgets
                                    .sort((a, b) => a.columnNumber - b.columnNumber)
                                    .map((widget) => (
                                        <Grid item xs={12} md={6} key={widget.widgetId}>
                                            <Card variant="outlined">
                                                <CardHeader
                                                    title={widget.widgetType
                                                        .replace(/_/g, ' ')
                                                        .replace(/\b\w/g, l => l.toUpperCase())
                                                    }
                                                    sx={{
                                                        backgroundColor: mode === 'dark' ? '#37474f' : '#6c89b5',
                                                        color: '#fff'
                                                    }}
                                                />
                                                <CardContent>{renderWidget(widget)}</CardContent>
                                            </Card>
                                        </Grid>
                                    ))}
                            </Grid>
                        ))
                ) : (
                    <Typography align="center" variant="body1" sx={{ mt: 5 }}>
                        🚫 There are no dashboards defined for your account.
                    </Typography>
                )}
            </Container>

            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={handleClosePopover}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
                <MenuItem onClick={() => selectedItem && handleOpen(selectedItem.item, selectedItem.type)}>
                    Open
                </MenuItem>
            </Popover>


            <Footer />
        </Box>
    );
};

export default Dashboard;
